import React, { useState, useMemo } from "react";
import { mockCreator } from "../../../../data/mockData";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import TextArea from "../../../../components/ui/TextArea";
import AdminDataTable from "../../../admin/components/AdminDataTable";
import { X, Send, Users, MessageSquare } from "lucide-react";

const BroadcastComposer = () => {
  const [channel, setChannel] = useState("Email");
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const availableAudiences = useMemo(() => {
    const tiers = mockCreator.subscriptionProducts.map((tier) => ({
      value: `tier:${tier.name}`,
      label: `Tier: ${tier.name}`,
    }));
    const groups = mockCreator.customAudienceGroups.map((group) => ({
      value: `group:${group}`,
      label: `Group: ${group}`,
    }));
    return [
      { value: "all_subscribers", label: "All Subscribers" },
      ...tiers,
      ...groups,
      { value: "event:Unity Concert", label: "Attendees: Unity Concert" },
    ];
  }, []);

  const handleAudienceChange = (e) => {
    const selectedValue = e.target.value;
    if (
      selectedValue &&
      !selectedAudiences.some((a) => a.value === selectedValue)
    ) {
      const audience = availableAudiences.find(
        (a) => a.value === selectedValue
      );
      setSelectedAudiences([...selectedAudiences, audience]);
    }
  };

  const removeAudience = (valueToRemove) => {
    setSelectedAudiences(
      selectedAudiences.filter((a) => a.value !== valueToRemove)
    );
  };

  const recipientCount =
    selectedAudiences.length === 0
      ? 2740
      : 150 + selectedAudiences.length * 250;
  const smsCost = recipientCount * 0.5;

  const historyColumns = [
    { 
      header: "Channel", 
      key: "channel", 
      render: (row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
          {row.channel}
        </span>
      )
    },
    { header: "Audience", key: "audience", render: (row) => row.audience },
    {
      header: "Subject/Message",
      key: "subject",
      render: (row) => <p className="truncate w-48 sm:w-64">{row.subject}</p>,
    },
    { header: "Sent", key: "sentDate", render: (row) => row.sentDate },
  ];

  return (
    <div className="@container space-y-8">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="p-2 bg-primary/10 rounded-lg text-primary">
            <Send size={20} />
          </div>
          <h2 className="text-xl font-bold text-slate-800">
            Send a New Broadcast
          </h2>
        </div>
        <p className="text-sm text-slate-500 ml-11">
          Reach your audience through multiple channels instantly.
        </p>
      </div>

      <div className="bg-slate-50 rounded-xl p-4 sm:p-6 border border-slate-200 space-y-6">
        <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Channel
            </label>
            <Select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              options={[
                { value: "Email", label: "Email Newsletter" },
                { value: "SMS", label: "SMS Blast" },
                { value: "Push Notification", label: "Push Notification" },
              ]}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Audience Segment
            </label>
            <div className="space-y-2">
              <Select
                onChange={handleAudienceChange}
                value=""
                options={[
                  { value: "", label: "Add audience segment..." },
                  ...availableAudiences
                    .filter(
                      (avail) =>
                        !selectedAudiences.some((sel) => sel.value === avail.value)
                    )
                    .map((opt) => ({ value: opt.value, label: opt.label })),
                ]}
              />
              
              <div className="flex flex-wrap gap-2">
                {selectedAudiences.map((audience) => (
                  <span
                    key={audience.value}
                    className="flex items-center bg-white border border-slate-200 text-slate-700 text-sm font-medium px-3 py-1 rounded-full shadow-sm"
                  >
                    {audience.label}
                    <button
                      onClick={() => removeAudience(audience.value)}
                      className="ml-2 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </span>
                ))}
              </div>
              
              <div className="flex flex-wrap items-center text-xs text-slate-500 mt-2 gap-1">
                <Users size={12} className="shrink-0" />
                <span className="whitespace-nowrap">Estimated recipients:</span>
                <strong className="text-slate-700">{recipientCount.toLocaleString()}</strong>
              </div>
            </div>
          </div>
        </div>

        {channel === "Email" && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Subject Line
            </label>
            <Input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter a catchy subject..."
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Message Content
          </label>
          <TextArea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows="6"
            placeholder={channel === "SMS" ? "Type your SMS message here..." : "Write your email content..."}
          />
          {channel === "SMS" && (
            <div className="mt-2 flex justify-between text-xs text-slate-500">
              <span>{message.length} characters</span>
              <span>~{Math.ceil(message.length / 160)} segments</span>
            </div>
          )}
        </div>

        {channel === "SMS" && (
          <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-800 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <MessageSquare size={16} />
              <span>Estimated Cost: <strong>{smsCost.toLocaleString()} ETB</strong></span>
            </div>
            <span>Credit: <strong>1,000 ETB</strong></span>
          </div>
        )}

        <div className="pt-4 border-t border-slate-200 flex justify-end">
          <Button variant="primary" size="lg" className="px-8">
            <Send size={18} className="mr-2" />
            Send Broadcast
          </Button>
        </div>
      </div>

      <div className="pt-8">
        <AdminDataTable
          title="Broadcast History"
          columns={historyColumns}
          data={mockCreator.broadcastHistory}
        />
      </div>
    </div>
  );
};

export default BroadcastComposer;
